import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import { BottomSheet, tokens, useIsMobile } from 'components/v3';
import Glyph from '../../Forms/Submissions/Glyph';
import { sx } from '../../Forms/Submissions/sx';
import { copyText } from '../shared/clipboard';
import { QR_SIZE, downloadQr, drawQr, qrFilename } from '../shared/qr';

// The W-9 link as a QR code (design W9Panels.QrModal, w9-plan T19): the
// code on a white card, the link under it, "Copy link" and "Download PNG".
// A bottom sheet on phones. QrCanvas is shared with the Link page.

export const QR_BLURB =
  'Print it for the sign-in window, pit gate, or payout booth. Drivers scan it and fill out their W-9 on their phone.';

export const QrCanvas = ({ text, size }) => {
  const ref = useRef(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    setFailed(false);
    if (!ref.current || !text) return undefined;
    Promise.resolve()
      .then(() => drawQr(ref.current, text, size))
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [text, size]);
  return (
    <div
      role="img"
      aria-label={`QR code for ${text}`}
      style={{
        padding: 12,
        borderRadius: 14,
        background: 'white',
        boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.14)',
        lineHeight: 0,
      }}
    >
      {failed ? (
        <div
          style={{
            width: size,
            height: size,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            lineHeight: 1.4,
            textAlign: 'center',
            color: 'rgba(15, 23, 42, 0.6)',
          }}
        >
          The QR code couldn&apos;t be drawn here. Download the PNG instead.
        </div>
      ) : (
        <canvas
          ref={ref}
          width={size}
          height={size}
          style={{ display: 'block' }}
        />
      )}
    </div>
  );
};

QrCanvas.propTypes = {
  text: PropTypes.string.isRequired,
  size: PropTypes.number,
};

QrCanvas.defaultProps = { size: QR_SIZE };

// Saves the PNG and reports it. Shared with the Link page.
export const saveQr = (url, slug, onToast) =>
  downloadQr(url, qrFilename(slug)).then(
    () => onToast({ type: 'success', title: 'QR code downloaded' }),
    () => onToast({ type: 'error', title: "Couldn't build the QR code" })
  );

const QrSheet = ({ open, url, slug, accountName, onClose, onToast }) => {
  const theme = useTheme();
  const t = tokens(theme);
  const s = sx(theme);
  const isMobile = useIsMobile();
  const buttonStyle = { width: isMobile ? '100%' : 'auto' };
  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="W-9 QR code"
      subtitle={accountName}
      footer={
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column-reverse' : 'row',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <ButtonV2
            onClick={() => copyText(url, 'Link', onToast)}
            icon={<Glyph name="copy" size={16} />}
            style={buttonStyle}
          >
            Copy link
          </ButtonV2>
          <ButtonV2
            primary
            onClick={() => saveQr(url, slug, onToast)}
            icon={<Glyph name="download" size={16} />}
            style={buttonStyle}
          >
            Download PNG
          </ButtonV2>
        </div>
      }
    >
      {open ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            padding: '4px 6px',
            textAlign: 'center',
          }}
        >
          <QrCanvas text={url} />
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: t.text,
              overflowWrap: 'anywhere',
            }}
          >
            {url.replace(/^https?:\/\//, '')}
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: 1.55,
              color: s.text2,
            }}
          >
            {QR_BLURB}
          </p>
        </div>
      ) : null}
    </BottomSheet>
  );
};

QrSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  url: PropTypes.string.isRequired,
  slug: PropTypes.string,
  accountName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onToast: PropTypes.func.isRequired,
};

QrSheet.defaultProps = { slug: null, accountName: null };

export default QrSheet;
